import React, { useState, useEffect } from "react";
import SEO from "../../pages/seo";
const CompleteStoreInformation = () => {
  const [activeSection, setActiveSection] = useState("store-name-contact");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "store-name-contact",
        "legal-business-address",
        "store-currency",
        "store-time-zone",
        "brand-assets",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  React.useEffect(() => {
    console.log("SEO Props:", {
      title: "Complete Your xPage Store Information - Setup Guide",
      description:
        "Step-by-step guide to complete your xPage store information including store name, contact details, currency, timezone, and brand assets setup.",
    });
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Complete Your xPage Store Information - Setup Guide"
        description="Step-by-step guide to complete your xPage store information including store name, contact details, currency, timezone, and brand assets setup."
        keywords="xPage store setup, ecommerce configuration, store information, business setup"
        canonical="https://help.xpage.ai/help/landing-page-setup/Complete%20Your%20xPage%20Store%20Information"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Complete Your xPage Store Information
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Create a professional and consistent store identity by completing your
          store details in xPage. This ensures your landing page, order emails,
          and admin dashboard reflect your brand accurately.
        </p>

        {/* Store Name and Contact Information */}
        <section id="store-name-contact" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Store Name and Contact Information
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Your store name appears throughout your storefront, emails, and
              dashboard. Contact email and phone number are used for
              notifications.
            </p>

            <h3 className="text-lg font-semibold text-systemTitle mb-2 mt-4">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel">
              <li className="mb-2">Log in to xPage Admin</li>
              <li className="mb-2">
                Go to <strong>Settings</strong> → <strong>General</strong> →{" "}
                <strong>Edit Info</strong>
              </li>
              <li className="mb-2">
                Update your store name, email, and phone number
              </li>
              <li className="mb-2">
                Click <strong>Save</strong>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Changing your store
                name does not update your default domain.
              </p>
            </div>
          </div>
        </section>

        {/* Legal Business Name and Address */}
        <section id="legal-business-address" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Legal Business Name and Billing Address
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              This information is used on invoices and billing statements and
              may be required for tax compliance.
            </p>

            <h3 className="text-lg font-semibold text-systemTitle mb-2 mt-4">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel">
              <li className="mb-2">Log in to xPage Admin</li>
              <li className="mb-2">
                Navigate to <strong>Settings</strong> → <strong>General</strong>{" "}
                → <strong>Company Name</strong> & <strong>Address</strong>
              </li>
              <li className="mb-2">
                Enter your legal business name and billing address
              </li>
              <li className="mb-2">
                Click <strong>Save</strong>
              </li>
            </ol>
          </div>
        </section>

        {/* Store Currency */}
        <section id="store-currency" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Select Store Currency
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Your default currency controls how prices and transactions display
              on product pages and checkout.
            </p>

            <h3 className="text-lg font-semibold text-systemTitle mb-2 mt-4">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel">
              <li className="mb-2">Log in to xPage Admin</li>
              <li className="mb-2">
                Go to <strong>Settings</strong> → <strong>General</strong> →{" "}
                <strong>Currency Dropdown</strong>
              </li>
              <li className="mb-2">
                Select your currency and click <strong>Save</strong>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Changing the
                currency does not automatically update product pricing. Adjust
                prices manually if needed.
              </p>
            </div>
          </div>
        </section>

        {/* Store Time Zone */}
        <section id="store-time-zone" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Store Time Zone
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Setting the correct time zone ensures accurate timestamps for
              orders, reports, and automated emails.
            </p>

            <h3 className="text-lg font-semibold text-systemTitle mb-2 mt-4">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel">
              <li className="mb-2">Log in to xPage Admin</li>
              <li className="mb-2">
                Go to <strong>Settings</strong> → <strong>General</strong> →{" "}
                <strong>Time Zone</strong>
              </li>
              <li className="mb-2">Select your region from the dropdown</li>
              <li className="mb-2">
                Click <strong>Save</strong>
              </li>
            </ol>
          </div>
        </section>

        {/* Brand Assets */}
        <section id="brand-assets" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Store Brand Assets
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Your brand identity builds recognition and trust. Configure your
              logo, brand colors, favicon, and social media links in xPage.
            </p>

            <h3 className="text-lg font-semibold text-systemTitle mb-2 mt-4">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel">
              <li className="mb-2">Log in to xPage Admin</li>
              <li className="mb-2">
                Go to <strong>Settings</strong> →{" "}
                <strong>Brand Identity</strong>
              </li>
              <li className="mb-2">
                Upload your logo, set primary and secondary brand colors, and
                add social media URLs
              </li>
              <li className="mb-2">
                Click <strong>Save</strong>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Your branding
                appears consistently across your storefront and landing pages.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#store-name-contact"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "store-name-contact"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Store Name & Contact
              </a>
            </li>
            <li>
              <a
                href="#legal-business-address"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "legal-business-address"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Legal Business Info
              </a>
            </li>
            <li>
              <a
                href="#store-currency"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "store-currency"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Store Currency
              </a>
            </li>
            <li>
              <a
                href="#store-time-zone"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "store-time-zone"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Store Time Zone
              </a>
            </li>
            <li>
              <a
                href="#brand-assets"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "brand-assets"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Brand Assets
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CompleteStoreInformation;
